import { create } from "zustand";

import type { LayoutResult } from "@/lib/types";

type State = {
  result: LayoutResult | null;
  pageIndex: number; // 0-based index into result.pages
  enabledTypes: Set<string>;
  showAbsorbed: boolean;
  showBackdrop: boolean;
  selectedId: string | null;
  isAnalyzing: boolean;
  error: string | null;
  setResult: (r: LayoutResult | null) => void;
  setPageIndex: (i: number) => void;
  toggleType: (t: string) => void;
  setAllTypes: (on: boolean) => void;
  setShowAbsorbed: (v: boolean) => void;
  setShowBackdrop: (v: boolean) => void;
  setSelected: (id: string | null) => void;
  setAnalyzing: (v: boolean) => void;
  setError: (msg: string | null) => void;
  reset: () => void;
};

function typesIn(result: LayoutResult | null): Set<string> {
  const s = new Set<string>();
  if (result) {
    for (const p of result.pages) {
      for (const e of p.elements) s.add(e.type);
    }
  }
  return s;
}

export const useLayoutStore = create<State>((set) => ({
  result: null,
  pageIndex: 0,
  enabledTypes: new Set(),
  showAbsorbed: false,
  showBackdrop: true,
  selectedId: null,
  isAnalyzing: false,
  error: null,
  setResult: (r) =>
    set({ result: r, pageIndex: 0, selectedId: null, enabledTypes: typesIn(r), error: null }),
  setPageIndex: (i) => set({ pageIndex: i, selectedId: null }),
  toggleType: (t) =>
    set((st) => {
      const next = new Set(st.enabledTypes);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return { enabledTypes: next };
    }),
  setAllTypes: (on) => set((st) => ({ enabledTypes: on ? typesIn(st.result) : new Set() })),
  setShowAbsorbed: (v) => set({ showAbsorbed: v }),
  setShowBackdrop: (v) => set({ showBackdrop: v }),
  setSelected: (id) => set({ selectedId: id }),
  setAnalyzing: (v) => set({ isAnalyzing: v }),
  setError: (msg) => set({ error: msg }),
  reset: () =>
    set({ result: null, pageIndex: 0, selectedId: null, error: null, enabledTypes: new Set() }),
}));
